import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";
const title = "Interim Management";
const heroImage = "https://net-basis.net/Bilder/ict-interim-management-experts-dach-market-entry.webp";
const heroAlt = "Businessman selects experienced ICT managers for sales and project leadership in the DACH market.";
const heroClaimLine1 = "Instant, flexible, expert, experienced ... temporary!";
const benefitsTitle = "How you benefit from our __Interim Management__ expertise:";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const widget2Text =
  "Your experienced partner for flexible Interim Management during market entry into the DACH region. We support foreign ICT manufacturers on-site, taking over specific tasks or your entire international project.";

const p1Text =
  "Various reasons can make \"Interim Management\" important for a company when entering new markets. The strategic goal of expanding business into new countries should take place independently of existing operations. The task must be approached quickly, flexibly, and according to demand, and is defined from the outset for a limited period only. Adapting to new markets and striving for a successful market position require specific knowledge, skills, and experience that internal employees cannot or should not fulfill.";
const p2Text =
  "This is precisely where NetBasis International helps its foreign manufacturing partners achieve desired or precisely defined international goals within a fixed timeframe. We do not only take over individual task areas (see Business Development), but upon request, the entire international project.";
const p2LinkText = "Business Development";
const p2LinkUrl = "/en/services/business-development";

const benefits = [
  "Fast on-site presence with minimal recruitment effort",
  "Absolute objectivity for your specific international venture",
  "Sound management experience and operational competence",
  "Strong market knowledge, skills, and direct contacts",
  "Focused and results-oriented work on your international goals",
  "Uncomplicated contractual relationships due to short-term availability and termination notice periods",
  "No competition or conflicts for internal executives",
  "Valuable personnel development effects depending on the project",
  "Absolute visibility of the incurred costs and our remuneration",
];

export default function InterimManagementEnPage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);
  const p2Parts = p2Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Interim Management | NetBasis International"
        description="Experienced temporary leadership competence to bridge vacancies or to manage critical expansion phases."
        ogUrl="https://net-basis.net/en/services/interim-management"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-start px-6 md:px-10 lg:pl-14 pointer-events-none mt-5 md:mt-10 lg:mt-10">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left animate-[interimTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  {heroClaimLine1}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              {widget2Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {(() => {
                  const parts = p2Text.split(strongName);
                  return parts.map((part, i) => {
                    const linkIdx = part.indexOf(p2LinkText);
                    if (linkIdx !== -1) {
                      const before = part.slice(0, linkIdx);
                      const after = part.slice(linkIdx + p2LinkText.length);
                      return (
                        <span key={i}>
                          {before}
                          <a href={p2LinkUrl} className="text-primary-600 hover:underline">{p2LinkText}</a>
                          {after}
                        </span>
                      );
                    }
                    return (
                      <span key={i}>
                        {part}
                        {i < parts.length - 1 && <strong className="text-primary-600">{strongName}</strong>}
                      </span>
                    );
                  });
                })()}
              </p>
            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}